import { isEqual } from "lodash";
import { makeAutoObservable, observable, remove } from "mobx";

export const QuotaOverrideOptions = [
  { value: "gvcs", label: "gvcs" },
  { value: "domains", label: "domains" },
  { value: "agents", label: "agents" },
  { value: "secrets", label: "secrets" },
  { value: "members", label: "members" },
  { value: "cloudaccounts", label: "cloudaccounts" },
  { value: "serviceaccounts", label: "serviceaccounts" },
  { value: "ipets", label: "ipets" },
  { value: "workloads-per-gvc", label: "workloads-per-gvc" },
  { value: "identities-per-gvc", label: "identities-per-gvc" },
  { value: "volumesets-per-gvc", label: "volumesets-per-gvc" },
  { value: "accounts-per-user", label: "accounts-per-user" },
  { value: "cpu-per-workload", label: "cpu-per-workload" },
  { value: "memory-per-workload", label: "memory-per-workload" },
  { value: "replicas-per-workload", label: "replicas-per-workload" },
];
export interface QuotaOverride {
  name: string;
  max: string;
}

export class OrgConfigModel {
  initialAwsPrivateLinks: string[] = [];
  initialGcpServiceConnects: string[] = [];
  initialQuotaOverrides: QuotaOverride[] = [];

  awsPrivateLinks = observable.array();
  gcpServiceConnects = observable.array();
  quotaOverrides = observable.array<QuotaOverride>();

  constructor() {
    makeAutoObservable(this);
  }

  setInitials(awsPrivateLinks: string[], gcpServiceConnects: string[], quotaOverrides: QuotaOverride[]) {
    // AWS
    this.awsPrivateLinks.clear();
    this.initialAwsPrivateLinks = [];
    for (let link of awsPrivateLinks) {
      this.initialAwsPrivateLinks.push(link);
    }

    // GCP
    this.gcpServiceConnects.clear();
    this.initialGcpServiceConnects = [];
    for (let link of gcpServiceConnects) {
      this.initialGcpServiceConnects.push(link);
    }

    // Quota Overrides
    this.quotaOverrides.clear();
    this.initialQuotaOverrides = [];
    for (let item of quotaOverrides) {
      this.initialQuotaOverrides.push({ name: item.name, max: item.max });
    }

    this.reset();
  }

  // AWS
  setAwsAt(index: number, value: string) {
    this.awsPrivateLinks[index] = value;
  }

  removeAwsAt(index: number) {
    remove(this.awsPrivateLinks, index);
  }

  addAws() {
    this.awsPrivateLinks.push("");
  }

  // GCP
  setGcpAt(index: number, value: string) {
    this.gcpServiceConnects[index] = value;
  }

  removeGcpAt(index: number) {
    remove(this.gcpServiceConnects, index);
  }

  addGcp() {
    this.gcpServiceConnects.push("");
  }

  // QuotaOverrides
  setQuotaOverrideNameAt(index: number, value: string) {
    this.quotaOverrides[index].name = value;
  }

  setQuotaOverrideMaxAt(index: number, value: string) {
    let safeValue = Number(value);
    if (Number.isNaN(safeValue)) {
      return;
    }
    this.quotaOverrides[index].max = value;
  }

  removeQuotaOverrideAt(index: number) {
    remove(this.quotaOverrides, index);
  }

  addQuotaOverride() {
    this.quotaOverrides.push({ name: "", max: "" });
  }

  reset() {
    this.awsPrivateLinks.clear();
    for (let link of this.initialAwsPrivateLinks) {
      this.awsPrivateLinks.push(link);
    }
    this.gcpServiceConnects.clear();
    for (let link of this.initialGcpServiceConnects) {
      this.gcpServiceConnects.push(link);
    }
    this.quotaOverrides.clear();
    for (let item of this.initialQuotaOverrides) {
      this.quotaOverrides.push({ name: item.name, max: item.max });
    }
  }

  confirm() {
    const object = this.asObject;

    this.initialAwsPrivateLinks = [];
    for (let link of object.data.awsPrivateLinks || []) {
      this.initialAwsPrivateLinks.push(link);
    }
    this.initialGcpServiceConnects = [];
    for (let link of object.data.gcpServiceConnects || []) {
      this.initialGcpServiceConnects.push(link);
    }
    this.initialQuotaOverrides = [];
    for (let item of object.data.quotaOverrides || []) {
      this.initialQuotaOverrides.push({ name: item.name, max: item.max });
    }

    this.reset();
  }

  get isDirty(): boolean {
    let res = false;
    if (
      !isEqual(this.initialAwsPrivateLinks, this.awsPrivateLinks) ||
      !isEqual(this.initialGcpServiceConnects, this.gcpServiceConnects) ||
      !isEqual(this.initialQuotaOverrides, this.quotaOverrides)
    ) {
      res = true;
    }
    return res;
  }

  get asObject() {
    const quotaOverrides: any[] = [];
    for (const quotaOverride of this.quotaOverrides) {
      if (!quotaOverride.max) {
        continue;
      }
      if (!quotaOverride.name) {
        continue;
      }
      if (Number.isNaN(Number(quotaOverride.max))) {
        continue;
      }

      quotaOverrides.push({
        name: quotaOverride.name,
        max: Number(quotaOverride.max),
      });
    }

    return {
      data: {
        awsPrivateLinks: this.awsPrivateLinks,
        gcpServiceConnects: this.gcpServiceConnects,
        quotaOverrides: quotaOverrides,
      },
    };
  }
}
